"use client";

import { IconHeading } from "@/components/platform/v2/IconHeading";
import { useState } from "react";
import { Check, Handshake, Star } from "lucide-react";
import { Section, SectionHeading } from "@/components/platform/v2/Section";
import { PageCta, PageHero } from "@/components/platform/v2/PageEdge";
import { Button } from "@/components/platform/v2/ui/button";
import PartnerApplicationForm, {
  type ProgramType,
} from "@/components/platform/v2/PartnerApplicationForm";

const programs = [
  {
    type: "partner" as ProgramType,
    icon: Handshake,
    title: "Партнёр",
    lead: "Специальные условия и вознаграждение за рекомендации.",
    facts: [
      "−50% на тарифы",
      "20% вознаграждение",
      "10 новых оплаченных клиентов VCENTRE.ONE / год",
    ],
  },
  {
    type: "ambassador" as ProgramType,
    icon: Star,
    title: "Амбассадор",
    lead: "Тарифный доступ и роль отраслевого примера.",
    facts: [
      "Тарифный доступ без оплаты",
      "Бонусные баллы",
      "20 новых оплаченных клиентов VCENTRE.ONE / год",
    ],
  },
];
const compare = [
  ["Тарифы", "−50%", "Без оплаты"],
  ["Денежное вознаграждение", "20%", "—"],
  ["Бонусные баллы", "Да", "Да"],
  ["Годовой KPI", "10 клиентов", "20 клиентов"],
  ["Срок", "12 месяцев", "12 месяцев"],
];
const PartnersPageComponent = () => {
  const [program, setProgram] = useState<ProgramType>("unsure");
  const apply = (p: ProgramType) => {
    setProgram(p);
    document
      .getElementById("application")
      ?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <div className="vcentre-public pt-24 sm:pt-28 md:pt-32">
      <PageHero
        eyebrow="Партнёрство VCENTRE.IT"
        title={
          <>
            Два формата.
            <br />
            <span className="text-primary">Один общий результат.</span>
          </>
        }
        text={
          <p>
            Используйте VCENTRE в своём бизнесе, рекомендуйте платформу и
            развивайте сильный отраслевой кейс.
          </p>
        }
        actions={
          <>
            <Button variant="site" onClick={() => apply("partner")}>
              Стать партнёром
            </Button>
            <a href="#compare" className="btn-ghost">
              Сравнить форматы
            </a>
          </>
        }
        visual={
          <img
            src={"/assets/images/platform/partners-mascot-hero.png"}
            alt="Маскот VCENTRE.IT с тайлом рукопожатия и медалью"
            width={1254}
            height={1254}
            loading="eager"
            className="w-full max-w-[330px] animate-float drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)] sm:max-w-[420px]"
          />
        }
      />
      <Section tone="quiet">
        <SectionHeading
          eyebrow="Партнёр vs Амбассадор"
          title="Что вы получаете"
        />
        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          {programs.map((p) => (
            <article key={p.title} className="surface flex flex-col">
              <IconHeading
                as="h2"
                icon={<p.icon className="h-7 w-7 text-primary" />}
                className="display-3"
              >
                {p.title}
              </IconHeading>
              <p className="lede mt-3">{p.lead}</p>
              <ul className="my-6 space-y-3">
                {p.facts.map((x) => (
                  <li key={x} className="flex gap-2 text-sm">
                    <Check className="h-4 w-4 text-primary" />
                    {x}
                  </li>
                ))}
              </ul>
              <Button
                variant="site"
                className="mt-auto"
                onClick={() => apply(p.type)}
              >
                {p.type === "partner"
                  ? "Стать партнёром"
                  : "Стать амбассадором"}
              </Button>
            </article>
          ))}
        </div>
        <div className="mt-6 text-center">
          <Button variant="link" onClick={() => apply("unsure")}>
            Помогите выбрать
          </Button>
        </div>
      </Section>
      <Section>
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <p className="eyebrow">Условия</p>
            <h2 className="display-2 mt-4">
              Что входит и что оплачивается отдельно
            </h2>
            <p className="body-text mt-5">
              Специальные условия действуют на тарифный доступ по договору.
              Расширения, AI-баланс сверх включённого, интеграции и
              индивидуальные работы оплачиваются отдельно.
            </p>
          </div>
          <div className="border-y border-border/60 py-3">
            {[
              "Рекомендация и закрепление клиента",
              "Оплата клиента фиксируется в системе",
              "Начисляется вознаграждение или баллы",
              "Годовой KPI учитывает только новых оплаченных клиентов",
              "Участник выполняет согласованные обязательства",
            ].map((x, i) => (
              <div
                key={x}
                className="flex gap-4 border-b border-border/50 py-4 last:border-0"
              >
                <span className="text-xs font-bold text-primary">0{i + 1}</span>
                <b className="text-sm">{x}</b>
              </div>
            ))}
          </div>
        </div>
      </Section>
      <Section tone="quiet">
        <SectionHeading
          eyebrow="Закрытая программа"
          title="Статус получают не автоматически"
          text="Команда оценивает бизнес, аудиторию, репутацию, потенциал кейса и готовность выполнять условия. Заявка не гарантирует участие."
        />
      </Section>
      <Section id="compare">
        <SectionHeading eyebrow="Сравнение" title="Ключевые различия" />
        <div className="mt-8 overflow-hidden rounded-2xl border border-border">
          <div className="grid grid-cols-3 bg-[hsl(var(--charcoal))] p-4 text-xs font-bold">
            <span>Условие</span>
            <span>Партнёр</span>
            <span>Амбассадор</span>
          </div>
          {compare.map((r) => (
            <div
              key={r[0]}
              className="grid grid-cols-3 border-t border-border p-4 text-sm"
            >
              <b>{r[0]}</b>
              <span>{r[1]}</span>
              <span>{r[2]}</span>
            </div>
          ))}
        </div>
      </Section>
      <Section tone="quiet" id="application">
        <SectionHeading eyebrow="Заявка" title="Расскажите о проекте" />
        <div className="surface mx-auto mt-8 max-w-4xl">
          <PartnerApplicationForm
            program={program}
            onProgramChange={setProgram}
          />
        </div>
      </Section>
      <Section>
        <PageCta
          title="Готовы обсудить сотрудничество?"
          text={
            <p>
              Выберите партнёрство, амбассадорство или попросите команду
              предложить формат.
            </p>
          }
          actions={
            <Button variant="site" onClick={() => apply("unsure")}>
              Обсудить сотрудничество
            </Button>
          }
          visual={
            <img
              src={"/assets/images/platform/partners-mascot-cta.png"}
              alt="Маскот VCENTRE.IT с наушниками и телефоном — всегда на связи"
              width={1254}
              height={1254}
              loading="lazy"
              className="w-full max-w-[260px] drop-shadow-[0_24px_48px_rgba(0,0,0,0.55)]"
            />
          }
        />
      </Section>
    </div>
  );
};
export default PartnersPageComponent;
